import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { streamPost } from "@/lib/api";

const SESSION_KEY = "keepthis_chat_session";
const SUGGESTIONS = ["What is KeepThis!?", "How does it work?", "When can I start?"];
const GREETING = { role: "assistant", content: "Hi! I'm the KeepThis! helper. Ask me anything about saving your child's artwork and memories." };

const getSession = () => {
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 64);
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const Bubble = ({ m, i }) => (
  <div data-testid={`chat-message-${i}`} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-navy text-cream rounded-br-md" : "bg-white text-navy rounded-bl-md shadow-sm"}`}>
    {m.content ? m.content.replace(/\*\*/g, "").replace(/^#+\s*/gm, "") : <Loader2 className="h-4 w-4 animate-spin text-teal" />}
  </div>
);

const ChatPanel = ({ onClose }) => {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const update = (content) => setMessages((m) => [...m.slice(0, -1), { role: "assistant", content }]);
    try {
      await streamPost("/ai/chat", { session_id: getSession(), message: q }, update);
    } catch (err) {
      update(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      role="dialog"
      aria-label="KeepThis! help chat"
      data-testid="chat-panel"
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.96 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      data-lenis-prevent
      className="fixed inset-x-3 bottom-3 z-[45] flex h-[min(560px,calc(100dvh-24px))] flex-col overflow-hidden rounded-[1.75rem] bg-cream shadow-[0_30px_90px_-25px_rgba(18,52,90,0.6)] ring-1 ring-navy/10 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]"
    >
      <div className="flex items-center justify-between bg-navy px-5 py-4">
        <div className="flex items-center gap-3">
          <img src="/images/logo-mark.webp" alt="" className="h-8 w-auto rounded-lg bg-cream p-0.5" />
          <div>
            <p className="font-heading font-extrabold text-cream">KeepThis! helper</p>
            <p className="text-xs text-cream/80">AI assistant. Answers may be imperfect.</p>
          </div>
        </div>
        <button type="button" data-testid="chat-close-button" aria-label="Close chat" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full text-cream transition-[background-color] hover:bg-white/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {messages.length === 1 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} type="button" data-testid={`chat-suggestion-${i}`} onClick={() => send(s)} className="rounded-full border border-teal/50 bg-white px-3 py-1.5 text-xs font-semibold text-teal-dark transition-[background-color] hover:bg-teal/10">{s}</button>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-navy/10 bg-white p-3">
        <label htmlFor="chat-input" className="sr-only">Your question</label>
        <input id="chat-input" data-testid="chat-input" value={input} maxLength={500} onChange={(e) => setInput(e.target.value)} placeholder="Ask a question…" className="h-11 flex-1 rounded-full bg-cream px-4 text-base text-navy placeholder:text-navy/60 focus:outline-none focus:ring-4 focus:ring-teal/20" />
        <button type="submit" data-testid="chat-send-button" aria-label="Send" disabled={busy || !input.trim()} className="flex h-11 w-11 items-center justify-center rounded-full bg-teal text-white transition-[background-color,opacity] hover:bg-teal-dark disabled:opacity-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </motion.div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AnimatePresence>{open && <ChatPanel onClose={() => setOpen(false)} />}</AnimatePresence>
      {!open && (
        <motion.button
          type="button"
          data-testid="chat-open-button"
          aria-label="Open help chat"
          onClick={() => setOpen(true)}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.06 }}
          transition={{ type: "spring", stiffness: 300, damping: 20, delay: 1.2 }}
          className="fixed bottom-24 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-navy text-cream shadow-[0_18px_40px_-12px_rgba(18,52,90,0.7)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40 md:bottom-6 md:right-6"
        >
          <MessageCircle className="h-6 w-6" />
        </motion.button>
      )}
    </>
  );
};
